// analysisTool.refactored.js
// ————————————————————————————————————————
// 1) 共用工具
// ————————————————————————————————————————
const METRICS = ["npRatio", "newPatients", "appointments"];
const METRIC_LABEL = {
  npRatio: "NP比例",
  newPatients: "NP數量",
  appointments: "預約數量",
};

function assertInputArray(arr, name = "input") {
  if (!Array.isArray(arr)) throw new Error(`${name} 應為陣列`);
}
function assertMetric(metric) {
  if (!METRICS.includes(metric)) {
    throw new Error(`metric 必須為 ${METRICS.join(" | ")}`);
  }
}
function assertNonEmptyString(v, name) {
  if (typeof v !== "string" || !v) {
    throw new Error(`${name} 必須是非空字串`);
  }
}
function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
function isFiniteNum(v) {
  const n = Number(v);
  return Number.isFinite(n);
}
/** 0~1 視為比率（乘 100），>1 視為已是百分比；非數字回傳 0 */
function normalizePercent(v) {
  let n = Number(v);
  if (!Number.isFinite(n)) return 0;
  if (n >= 0 && n <= 1) n = n * 100;
  return n;
}
function sortZH(a, b) {
  return a.localeCompare(b, "zh-Hant", { sensitivity: "base", numeric: true });
}
/** 穩定 hash → rgba，用於 Chart.js 顏色 */
function rgbaFromString(str, alpha = 0.7) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const r = (h & 0xff);
  const g = ((h >>> 8) & 0xff);
  const b = ((h >>> 16) & 0xff);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
/** 蒐集所有月份（YYYY-MM）並排序 */
function collectMonths(input, eachByDate) {
  const monthSet = new Set();
  for (const doc of input) {
    for (const item of eachByDate(doc) || []) {
      const m = item?.date;
      if (m) monthSet.add(m);
    }
  }
  return Array.from(monthSet).sort();
}
/** 建立 ECharts 通用雙軸（左數量／右百分比） */
function makeEchartsDualAxis(useRightAxis) {
  return [
    { name: "數量", type: "value", position: "left" },
    { name: "百分比", type: "value", position: "right", axisLabel: { formatter: "{value} %" } },
  ];
}

// ————————————————————————————————————————
// 2) 折線圖（ECharts）泛用建構器
//    目標：統一 total / bySource / byResult / byAppointmentItem
// ————————————————————————————————————————
/**
 * @param {Array} input            - doctors 陣列
 * @param {"npRatio"|"newPatients"|"appointments"} metric
 * @param {(doc:any)=>Array} eachByDate - 取得該 doc 的 byDate 陣列
 * @param {(byDateItem:any)=>any|null} getBucket - 從單一 byDateItem 取出要看的 bucket（可能是 item.data / item.bySource[key] / ...）
 * @param {string} legendSuffix     - 圖例顯示在醫師名後的括號內容，如 "(新病比率)"
 * @param {number} percentDigits    - 表格百分比小數位
 * @param {string} chartName    - tab 名稱
 * @returns {{ name:string, chartType:"line", chartOptions:any, tableData:{headers:string[],items:Array<Record<string,any>>}}}
 */
function buildDoctorLineChartGeneric(input, metric, eachByDate, getBucket, legendSuffix, percentDigits = 2, chartName='') {
  assertInputArray(input, "input");
  assertMetric(metric);

  // 1) 蒐集月份
  const months = collectMonths(input, eachByDate);

  // 2) 醫師清單與 doctor->(month->value)
  const names = [];
  const doctorMonthValue = new Map();

  for (const doc of input) {
    const name = doc?.name;
    if (!name) continue;
    names.push(name);

    const monthMap = new Map();
    for (const item of eachByDate(doc) || []) {
      const m = item?.date;
      const bucket = getBucket(item);
      if (!m || !bucket) continue;

      let val = toNum(bucket[metric]);
      if (metric === "npRatio") val = normalizePercent(val);
      monthMap.set(m, (monthMap.get(m) || 0) + val);
    }
    doctorMonthValue.set(name, monthMap);
  }

  const sortedDoctors = Array.from(new Set(names)).filter(Boolean).sort(sortZH);
  const useRightAxis = metric === "npRatio";
  const metricLabel = METRIC_LABEL[metric];

  // 3) series / legend
  const series = sortedDoctors.map((name) => {
    const mm = doctorMonthValue.get(name);
    const data = months.map((ym) => toNum(mm?.get(ym)));
    return { name: `${name} (${metricLabel})`, type: "line", data, yAxisIndex: useRightAxis ? 1 : 0, smooth: true };
  });
  const legendData = series.map(s => s.name);

  // 4) 表格
  const headers = ["月份", ...sortedDoctors];
  const items = months.map((ym) => {
    const row = { 月份: ym };
    for (const name of sortedDoctors) {
      const v = toNum(doctorMonthValue.get(name)?.get(ym));
      row[name] = useRightAxis ? `${v.toFixed(percentDigits)}%` : v;
    }
    return row;
  });

  // 5) 輸出
  return {
    name: chartName || '折線圖',
    chartType: "line",
    chartOptions: {
      tooltip: { trigger: "axis" },
      legend: { type: "scroll", data: legendData },
      xAxis: { type: "category", data: months },
      yAxis: makeEchartsDualAxis(useRightAxis),
      series,
    },
    tableData: { headers, items },
  };
}

// ————————————————————————————————————————
// 3) 混合圖（Chart.js）泛用建構器：醫師 × 類別（來源/結果/項目）跨月加總
// ————————————————————————————————————————
/**
 * 聚合層級：Doctor × Category；左軸堆疊：appointments（可分舊/新），右軸：NP 比例
 * @param {Array} input
 * @param {(doc:any)=>Array} eachByDate
 * @param {(byDateItem:any)=>Record<string, any>} getCategoryBuckets  回傳 { [categoryName]: payload }
 * @param {string[]} [doctorsOrder]
 * @param {string[]} [categoriesOrder]
 * @param {number} [percentDigits=1]
 * @param {string} [stackName="Stack 1"]
 * @returns {{ name:string, chartType:"mixed", chartData:any, chartOptions:any, tableData:{headers:string[],items:any[]} }}
 */
// function buildDoctorCategoryMixedChartGeneric(
//   input,
//   eachByDate,
//   getCategoryBuckets,
//   { doctorsOrder, categoriesOrder, percentDigits = 1, stackName = "Stack 1", chartName = '混合圖' } = {}
// ) {
//   assertInputArray(input, "input");
//
//   // 彙總：agg[doctor][category] = { apptSum, npSum, oldSum }
//   const doctorSet = new Set();
//   const categorySet = new Set();
//   const agg = Object.create(null);
//
//   for (const doc of input) {
//     const doctorName = doc?.name;
//     if (!doctorName) continue;
//     doctorSet.add(doctorName);
//
//     for (const item of eachByDate(doc) || []) {
//       const buckets = getCategoryBuckets(item) || {};
//       for (const [cat, payload] of Object.entries(buckets)) {
//         categorySet.add(cat);
//         const appt = toNum(payload.appointments);
//         const np   = toNum(payload.newPatients);
//         const oldP = toNum(payload.oldPatients); // 某些來源可能沒有 oldPatients，toNum 會回 0
//
//         if (!agg[doctorName]) agg[doctorName] = Object.create(null);
//         if (!agg[doctorName][cat]) {
//           agg[doctorName][cat] = { apptSum: 0, npSum: 0, oldSum: 0 };
//         }
//         const cell = agg[doctorName][cat];
//         cell.apptSum += appt;
//         cell.npSum   += np;
//         cell.oldSum  += oldP;
//       }
//     }
//   }
//
//   const labels = doctorsOrder?.length ? doctorsOrder.slice() : Array.from(doctorSet).sort(sortZH);
//   const cats   = categoriesOrder?.length ? categoriesOrder.slice() : Array.from(categorySet).sort(sortZH);
//
//   // Chart.js datasets：每個類別一條 bar（顯示 appointments=old+new），再加一條 scatter/line 顯示 NP%
//   const datasets = cats.map((cat) => {
//     const data = labels.map((doctor) => agg[doctor]?.[cat]?.apptSum ?? 0);
//     return { label: cat, data, backgroundColor: rgbaFromString(cat, 0.7), yAxisID: "y-axis-1", stack: stackName, type: "bar" };
//   });
//
//   // 表格：每列（醫師×類別）
//   const tableItems = [];
//   for (const doctor of labels) {
//     for (const cat of cats) {
//       const cell = agg[doctor]?.[cat];
//       const appt = cell?.apptSum ?? 0;
//       const np   = cell?.npSum ?? 0;
//       const ratioPct = appt > 0 ? (np / appt) * 100 : 0;
//
//       tableItems.push({
//         "醫師": doctor,
//         "類別": cat,
//         "預約數": String(appt),
//         "NP數": String(np),
//         "NP比例": `${ratioPct.toFixed(percentDigits)}%`,
//       });
//     }
//   }
//
//   return {
//     name: chartName,
//     chartType: "mixed",
//     chartData: { labels, datasets },
//     chartOptions: {
//       responsive: true,
//       maintainAspectRatio: false,
//       scales: {
//         yAxes: [
//           { id: "y-axis-1", type: "linear", position: "left", ticks: { beginAtZero: true }, scaleLabel: { display: true, labelString: "預約數量" }, stacked: true },
//           // { id: "y-axis-2", type: "linear", position: "right", ticks: { beginAtZero: true }, gridLines: { drawOnChartArea: false } },
//         ],
//         xAxes: [{ stacked: true }],
//       },
//       tooltips: { mode: "index", intersect: false },
//     },
//     tableData: {
//       headers: ["醫師", "類別", "預約數", "NP數", "NP比例"],
//       items: tableItems,
//     },
//   };
// }

/**
 * 聚合層級：Doctor × Category；左軸堆疊：appointments（可分舊/新），可選右軸顯示「NP 比例（醫師總和）」點/線
 * 備註：預設不畫右軸比例；要的話傳 computeRatio: true
 */
function buildDoctorCategoryMixedChartGeneric(
  input,
  eachByDate,
  getCategoryBuckets,
  {
    doctorsOrder,
    categoriesOrder,
    percentDigits = 1,
    stackName = "Stack 1",
    chartName = "混合圖",
    // --- 新增控制 ---
    showRightAxis = false,        // 右軸是否顯示（僅在 computeRatio=true 有意義）
    computeRatio = false,         // 是否加一組「每位醫師的 NP%（跨所有類別加總）」點/線
    ratioAs = "scatter",          // 'scatter' | 'line'
    leftAxisName = "預約數量",
    rightAxisName = "NP比例",
    actorHeader = "醫師",
    categoryHeader = "類別",
  } = {}
) {
  assertInputArray(input, "input");

  // 彙總：agg[doctor][category] = { apptSum, npSum, oldSum }
  const doctorSet = new Set();
  const categorySet = new Set();
  const agg = Object.create(null);

  for (const doc of input) {
    const doctorName = doc?.name;
    if (!doctorName) continue;
    doctorSet.add(doctorName);

    for (const item of eachByDate(doc) || []) {
      const buckets = getCategoryBuckets(item) || {};
      for (const [cat, payload] of Object.entries(buckets)) {
        categorySet.add(cat);
        const appt = toNum(payload.appointments);
        const np   = toNum(payload.newPatients);
        const oldP = toNum(payload.oldPatients);

        if (!agg[doctorName]) agg[doctorName] = Object.create(null);
        if (!agg[doctorName][cat]) {
          agg[doctorName][cat] = { apptSum: 0, npSum: 0, oldSum: 0 };
        }
        const cell = agg[doctorName][cat];
        cell.apptSum += appt;
        cell.npSum   += np;
        cell.oldSum  += oldP;
      }
    }
  }

  const labels = doctorsOrder?.length ? doctorsOrder.slice() : Array.from(doctorSet).sort(sortZH);
  const cats   = categoriesOrder?.length ? categoriesOrder.slice() : Array.from(categorySet).sort(sortZH);

  // ECharts series：每個「類別」一條 bar（顯示 appointments=old+new 的總和）
  const series = cats.map((cat) => {
    const data = labels.map((doctor) => agg[doctor]?.[cat]?.apptSum ?? 0);
    return { name: cat, type: "bar", stack: stackName, data };
  });

  // 可選：再加一條「醫師整體 NP%」的 scatter/line（以 doctor 維度跨類別加總）
  let ratioData = null;
  if (computeRatio) {
    ratioData = labels.map((doctor) => {
      const byCat = agg[doctor] || {};
      let appt = 0, np = 0;
      for (const cat of Object.keys(byCat)) {
        appt += byCat[cat].apptSum || 0;
        np   += byCat[cat].npSum   || 0;
      }
      return appt > 0 ? (np / appt) * 100 : 0;
    });
    series.push({
      name: rightAxisName,
      type: ratioAs === "line" ? "line" : "scatter",
      data: ratioData,
      yAxisIndex: showRightAxis ? 1 : 0,
      showSymbol: true,
      symbolSize: 8,
      ...(ratioAs === "line" ? { smooth: true } : null),
    });
  }

  // y 軸
  const yAxis = [{ type: "value", name: leftAxisName, min: 0 }];
  if (computeRatio) {
    if (showRightAxis) {
      yAxis.push({ type: "value", name: rightAxisName, position: "right", axisLabel: { formatter: "{value} %" }, splitLine: { show: false }, });
    } else {
      // 右軸不顯示，但保留比例縮放可改掛左軸就好（簡單起見，這裡直接讓比例走左軸 0~100）
      // 若要更精細：你也可以保留右軸但把 axisLabel/line/tick/splitLine 全部隱藏
      yAxis[0].max = typeof yAxis[0].max === "number" ? Math.max(100, yAxis[0].max) : 100;
    }
  }

  // tooltip：比例加 '%' 
  const option = {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: function (params) {
        const lines = [params[0]?.axisValueLabel || ""];
        params.forEach((p) => {
          const isRatio = computeRatio && (p.seriesName === rightAxisName);
          const val = p.value == null ? "-" : p.value;
          lines.push(`${p.marker}${p.seriesName}: ${isRatio ? Number(val).toFixed(percentDigits) + "%" : val}`);
        });
        return lines.join("<br/>");
      },
    },
    legend: { type: "scroll" },
    grid: { top: 32, right: showRightAxis ? 56 : 16, left: 56, bottom: 24, containLabel: true },
    xAxis: {
      type: "category",
      data: labels,
      axisLabel: {
        interval: 0,        // ← 強制每個類別都顯示
        // rotate: 30,      // 空間不夠時可斜排（可 20~45）
        // formatter: v => v.replace(/(.{6})/g, '$1\n'), // 太長就換行
        // fontSize: 11,    // 需要時可調小字級
      }
    },
    yAxis,
    series,
  };

  // 表格：每列（醫師×類別）
  const tableItems = [];
  for (const doctor of labels) {
    for (const cat of cats) {
      const cell = agg[doctor]?.[cat];
      const appt = cell?.apptSum ?? 0;
      const np   = cell?.npSum ?? 0;
      const ratioPct = appt > 0 ? (np / appt) * 100 : 0;
      tableItems.push({
        [actorHeader]: doctor,
        [categoryHeader]: cat,
        "預約數": String(appt),
        "NP數": String(np),
        "NP比例": `${ratioPct.toFixed(percentDigits)}%`,
      });
    }
  }

  return {
    name: chartName,
    chartType: "mixed",
    // 直接給 ECharts 的 option
    chartOptions: option,
    // 仍保留表格資料
    tableData: { headers: [actorHeader, categoryHeader, "預約數", "NP數", "NP比例"], items: tableItems },
  };
}



// ————————————————————————————————————————
// 4) 對外 API（保留你原本語意，內部走泛用器）
// ————————————————————————————————————————

// A. 總覽：[{ name, byDate:[{date, data:{...}}] }]
export function buildDoctorTotalAppointmentLineChartData(input, metric, opts = {}) {
  assertInputArray(input, "input");
  assertMetric(metric);
  const { percentDigits = 2, chartName } = opts;
  const legendSuffix = "";
  return buildDoctorLineChartGeneric(
    input,
    metric,
    (doc) => doc?.byDate || [],
    (item) => item?.data || null,
    legendSuffix,
    percentDigits,
    chartName
  );
}

// B. 來源折線圖：需傳 sourceKey
export function buildDoctorSourceLineChartData(input, sourceKey, metric, opts = {}) {
  assertInputArray(input, "input");
  assertNonEmptyString(sourceKey, "sourceKey");
  assertMetric(metric);
  const { percentDigits = 2, chartName } = opts;
  return buildDoctorLineChartGeneric(
    input,
    metric,
    (doc) => doc?.byDate || [],
    (item) => item?.bySource?.[sourceKey] || null,
    sourceKey,
    percentDigits,
    chartName
  );
}

// C. 處理結果折線圖：需傳 resultKey
export function buildDoctorProcessingResultLineChartData(input, resultKey, metric, opts = {}) {
  assertInputArray(input, "input");
  assertNonEmptyString(resultKey, "resultKey");
  assertMetric(metric);
  const { percentDigits = 2, chartName } = opts;
  return buildDoctorLineChartGeneric(
    input,
    metric,
    (doc) => doc?.byDate || [],
    (item) => item?.byResult?.[resultKey] || null,
    resultKey,
    percentDigits,
    chartName
  );
}

// D. 療程項目折線圖：需傳 appointmentItemKey
export function buildDoctorAppointmentItemLineChart(input, appointmentItemKey, metric, opts = {}) {
  assertInputArray(input, "input");
  assertNonEmptyString(appointmentItemKey, "appointmentItemKey");
  assertMetric(metric);
  const { percentDigits = 2, chartName } = opts;
  return buildDoctorLineChartGeneric(
    input,
    metric,
    (doc) => doc?.byDate || [],
    (item) => item?.byAppointmentItem?.[appointmentItemKey] || null,
    appointmentItemKey,
    percentDigits,
    chartName
  );
}

// E. 來源混合圖（醫師 × 來源）
export function buildDoctorSourceMixedChart(input, opts = {}) {
  return buildDoctorCategoryMixedChartGeneric(
    input,
    (doc) => doc?.byDate || [],
    (item) => item?.bySource || {},
    { ...opts, categoryHeader: "來源" }
  );
}

// F. 處理結果混合圖（醫師 × 結果）
export function buildDoctorProcessingResultMixedChart(input, opts = {}) {
  return buildDoctorCategoryMixedChartGeneric(
    input,
    (doc) => doc?.byDate || [],
    (item) => item?.byResult || {},
    { ...opts, categoryHeader: "處理結果" }
  );
}

// G. 療程項目混合圖（醫師 × 項目）
export function buildDoctorAppointmentItemMixedChart(input, opts = {}) {
  return buildDoctorCategoryMixedChartGeneric(
    input,
    (doc) => doc?.byDate || [],
    (item) => item?.byAppointmentItem || {},
    { ...opts, categoryHeader: "治療項目" }
  );
}

// H. 總覽混合圖（醫師 × 總預約）
export function buildDoctorTotalAppointmentMixedChart(input, opts = {}) {
  assertInputArray(input, "input");

  const {
    doctorsOrder,
    stackName = "Stack 1",
    percentDigits = 1,
    dateFrom,
    dateTo,
    // 新增：ECharts 相關控制
    computeRatio = true,        // 是否顯示 NP 比例系列（預設 true，與你原本一致）
    showRightAxis = true,       // 是否顯示右側 y 軸（比例用）
    ratioAs = "scatter",        // 'scatter' | 'line'
    ratioSmooth = true,         // ratioAs='line' 時是否平滑
    leftAxisName = "預約數量",
    rightAxisName = "NP比例",
    palette,                    // 可選：色盤（若提供會覆蓋主題的 color）
    actorHeader = "醫師",
  } = opts;

  const inRange = (ym) => {
    if (!ym) return false;
    if (dateFrom && ym < dateFrom) return false;
    if (dateTo && ym > dateTo) return false;
    return true;
  };
  const useDateFilter = Boolean(dateFrom || dateTo);

  // 彙總每位醫師
  const doctorSet = new Set();
  const agg = Object.create(null); // name -> { oldSum, newSum, apptSum }
  for (const doc of input) {
    const name = doc?.name;
    if (!name) continue;
    doctorSet.add(name);

    for (const by of (doc?.byDate || [])) {
      if (useDateFilter && !inRange(by?.date)) continue;
      const p = by?.data || {};
      const oldP = toNum(p.oldPatients);
      const newP = toNum(p.newPatients);
      const appt = toNum(p.appointments);

      if (!agg[name]) agg[name] = { oldSum: 0, newSum: 0, apptSum: 0 };
      const cell = agg[name];
      cell.oldSum  += oldP;
      cell.newSum  += newP;
      cell.apptSum += appt;
    }
  }

  const labels = doctorsOrder?.length ? doctorsOrder.slice() : Array.from(doctorSet).sort(sortZH);

  const oldSeries  = labels.map((d) => agg[d]?.oldSum  ?? 0);
  const newSeries  = labels.map((d) => agg[d]?.newSum  ?? 0);
  const ratioSeries = labels.map((d) => {
    const c = agg[d];
    if (!c) return 0;
    return c.apptSum > 0 ? (c.newSum / c.apptSum) * 100 : 0;
  });

  // ——— ECharts series ———
  const series = [
    { name: "舊病人", type: "bar", stack: stackName, data: oldSeries },
    { name: "新病人", type: "bar", stack: stackName, data: newSeries },
  ];
  if (computeRatio) {
    series.push({
      name: rightAxisName,
      type: ratioAs === "line" ? "line" : "scatter",
      data: ratioSeries,
      yAxisIndex: 1,                 // 維持右軸縮放
      showSymbol: true,
      symbolSize: 8,
      ...(ratioAs === "line" ? { smooth: !!ratioSmooth } : null),
    });
  }

  // ——— yAxis ———
  const yAxis = [{ type: "value", name: leftAxisName, min: 0 }];
  if (computeRatio) {
    if (showRightAxis) {
      yAxis.push({
        type: "value",
        name: rightAxisName,
        position: "right",
        axisLabel: { formatter: "{value} %" },
        splitLine: { show: false },
      });
    } else {
      // 右軸存在但隱藏（提供比例縮放，不顯示刻度/線條）
      yAxis.push({
        type: "value",
        position: "right",
        min: 0, max: 100,
        axisLabel: { show: false },
        axisLine:  { show: false },
        axisTick:  { show: false },
        splitLine: { show: false },
      });
    }
  }

  // ——— tooltip ———
  const option = {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: function (params) {
        const lines = [params[0]?.axisValueLabel || ""];
        params.forEach((p) => {
          const isRatio = computeRatio && (p.seriesName === rightAxisName);
          const val = p.value == null ? "-" : p.value;
          lines.push(`${p.marker}${p.seriesName}: ${isRatio ? Number(val).toFixed(percentDigits) + "%" : val}`);
        });
        return lines.join("<br/>");
      },
    },
    legend: { type: "scroll" },
    grid: { top: 32, right: showRightAxis ? 56 : 16, left: 56, bottom: 24, containLabel: true },
    xAxis: {
      type: "category",
      data: labels,
      axisLabel: {
        interval: 0,        // ← 強制每個類別都顯示
        // rotate: 30,      // 空間不夠時可斜排（可 20~45）
        // formatter: v => v.replace(/(.{6})/g, '$1\n'), // 太長就換行
        // fontSize: 11,    // 需要時可調小字級
      }
    },
    yAxis,
    series,
  };

  if (palette && palette.length) {
    option.color = palette
  }

  // 表格（維持原本介面）
  const tableItems = labels.map((name) => {
    const c = agg[name] || { oldSum: 0, newSum: 0, apptSum: 0 };
    const ratio = c.apptSum > 0 ? (c.newSum / c.apptSum) * 100 : 0;
    return {
      [actorHeader]: name,
      "總預約數": c.apptSum,
      "舊病人數": c.oldSum,
      "新病人數": c.newSum,
      "NP比例": `${ratio.toFixed(percentDigits)}%`,
    };
  });

  return {
    name: "NP結構",
    chartType: "mixed",
    // 改為回傳 ECharts 的 option（給 EchartsMixed 直接 setOption）
    chartOptions: option,
    tableData: { headers: [actorHeader, "總預約數", "舊病人數", "新病人數", "NP比例"], items: tableItems },
  };
}

// ————————————————————————————————————————
// 5) 額外：來源為主（X=來源）的匯總混合圖（保留你原函式語意）
//    支援 dateFrom/dateTo 篩選，並維持 tooltip 百分比格式
// ————————————————————————————————————————
export function buildSourceAxisMixedChart(dataList, opts = {}) {
  assertInputArray(dataList, "dataList");
  const { sourcesOrder, stackName = "Stack 1", percentDigits = 1, dateFrom, dateTo } = opts;

  const inRange = (ym) => {
    if (!ym) return false;
    if (dateFrom && ym < dateFrom) return false;
    if (dateTo && ym > dateTo) return false;
    return true;
  };
  const useDateFilter = Boolean(dateFrom || dateTo);

  const perSource = Object.create(null);
  const sourceSet = new Set();

  for (const doc of dataList) {
    for (const by of (doc?.byDate || [])) {
      const ym = by?.date;
      if (useDateFilter && !inRange(ym)) continue;

      const buckets = by?.bySource || {};
      for (const [src, payload] of Object.entries(buckets)) {
        sourceSet.add(src);
        const oldP = toNum(payload.oldPatients);
        const newP = toNum(payload.newPatients);
        const appt = toNum(payload.appointments);

        if (!perSource[src]) {
          perSource[src] = { oldSum: 0, newSum: 0, apptSum: 0, ratioProvided: false, ratioValSum: 0, ratioCount: 0 };
        }
        const cell = perSource[src];
        cell.oldSum += oldP;
        cell.newSum += newP;
        cell.apptSum += appt;

        const r = payload?.npRatio;
        if (isFiniteNum(r)) {
          cell.ratioProvided = true;
          cell.ratioValSum += normalizePercent(r);
          cell.ratioCount  += 1;
        }
      }
    }
  }

  const labels = sourcesOrder?.length ? sourcesOrder.slice() : Array.from(sourceSet).sort(sortZH);

  const oldSeries = labels.map((s) => perSource[s]?.oldSum ?? 0);
  const newSeries = labels.map((s) => perSource[s]?.newSum ?? 0);
  const ratioSeries = labels.map((s) => {
    const c = perSource[s];
    if (!c) return 0;
    if (c.ratioProvided && c.ratioCount > 0) return c.ratioValSum / c.ratioCount;
    return c.apptSum > 0 ? (c.newSum / c.apptSum) * 100 : 0;
  });

  const datasets = [
    { label: "舊病人", data: oldSeries, type: "bar", backgroundColor: "rgba(54, 162, 235, 0.5)", yAxisID: "y-axis-1", stack: stackName },
    { label: "新病人", data: newSeries, type: "bar", backgroundColor: "rgba(255, 99, 132, 0.5)", yAxisID: "y-axis-1", stack: stackName },
    {
      label: "NP比例",
      data: ratioSeries,
      type: "line",
      yAxisID: "y-axis-2",
      showLine: false,
      fill: false,
      borderColor: "rgba(0,0,0,0)",
      pointBackgroundColor: "rgba(75, 192, 192, 1)",
      pointBorderColor: "#fff",
      pointRadius: 5,
    },
  ];

  const tableItems = labels.map((src) => {
    const c = perSource[src] || { oldSum: 0, newSum: 0, apptSum: 0 };
    const ratio = c.apptSum > 0 ? (c.newSum / c.apptSum) * 100 : 0;
    return {
      "來源": src,
      "總預約數": c.apptSum,
      "舊病人數": c.oldSum,
      "新病人數": c.newSum,
      "NP比例": `${ratio.toFixed(percentDigits)}%`,
    };
  });

  return {
    name: "行銷管道效益",
    chartType: "mixed",
    chartData: { labels, datasets },
    chartOptions: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        yAxes: [
          { id: "y-axis-1", type: "linear", position: "left", ticks: { beginAtZero: true }, scaleLabel: { display: true, labelString: "預約數量" }, stacked: true },
          { id: "y-axis-2", type: "linear", position: "right", ticks: { beginAtZero: true, callback: (v) => v + "%" }, gridLines: { drawOnChartArea: false }, scaleLabel: { display: true, labelString: "NP比例" } },
        ],
        xAxes: [{ stacked: true }],
      },
      tooltips: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: function(tooltipItem, data) {
            const ds = data.datasets[tooltipItem.datasetIndex];
            if (ds && ds.label === "NP比例") {
              return `${ds.label}: ${Number(tooltipItem.value).toFixed(percentDigits)}%`;
            }
            return `${ds.label}: ${tooltipItem.value}`;
          }
        },
      },
    },
    tableData: { headers: ["來源", "總預約數", "舊病人數", "新病人數", "NP比例"], items: tableItems },
  };
}

export function buildWebTrafficViewsLineChart(input, opts = {}) {
  if (!Array.isArray(input)) {
    throw new Error("input 應為陣列");
  }

  const dates = input.map(item => item.month);
  const views = input.map(item => Number(item.views) || 0);

  return {
    name: "折線圖",
    chartType: "line",
    chartOptions: {
      tooltip: { trigger: "axis" },
      xAxis: {
        type: "category",
        data: dates,
      },
      yAxis: {
        type: "value",
        name: "瀏覽數",
      },
      series: [
        {
          name: "瀏覽數",
          type: "line",
          data: views,
          yAxisIndex: 0,
          smooth: true
        }
      ]
    },
    tableData: {
      headers: ["月份", "瀏覽數"],
      items: dates.map((d, i) => ({
        "月份": d,
        "瀏覽數": views[i]
      })),
    }
  };
}
